import { useEffect, useMemo } from "react"
import axios from 'axios';
import * as turf from "@turf/turf";
import {
    Source,
    Layer,
    Popup
} from 'react-map-gl/maplibre';
import Table from 'react-bootstrap/Table';
import { formatDate } from '../CustomDateTools';
import { displayTenementHolder } from './TenementOverlay';

const SlipWfsServer = axios.create({
    baseURL: "https://public-services.slip.wa.gov.au/public/services/SLIP_Public_Services/Industry_and_Mining_WFS/MapServer/WFSServer",
    timeout: 30000
});

/**
 * DMIRS writes tenement ids padded and with a double space ("E  2801895 ",
 * "E 28/1895       "); the tenement layer has them trimmed ("E 28/1895"). One
 * key for both.
 */
export const tenementKey = (id) => String(id || '').trim().replace(/\s+/g, ' ').toUpperCase();

const dateCell = (v) => {
    const t = Date.parse(v);
    return Number.isFinite(t) ? formatDate(new Date(t)) : '—';
};

/**
 * Popup for an amalgamation-pending tenement.
 *
 * The SLIP amalgamation layer (DMIRS-031) carries only the tenement id, its
 * type, the amalgamation id and when it went pending - no holder, status or
 * dates. Those come from the tenement register (ne:wa_tenements), joined in by
 * AmalgamationsPendingOverlay as t_* properties, so this shows the same
 * details as a tenement's own popup, plus the amalgamation.
 */
export function createAmalgamationsPendingPopup(feature, latLng, setClosed) {
    const p = feature.properties;
    const areaHa = p.t_legal_area != null
        ? `${p.t_legal_area} (${p.t_unit_of_me || 'HA'})`
        : `${(turf.area(feature) / 10000).toFixed(2)} HA (mapped)`;

    return (
        <Popup
            latitude={latLng.lat}
            longitude={latLng.lng}
            anchor="bottom"
            onClose={() => setClosed(false)}
        >
            <Table striped bordered style={{ marginBottom: 0 }}>
                <tbody>
                    <tr><td>ID:</td><td>{tenementKey(p.fmt_tenid)}</td></tr>
                    {p.t_type && <tr><td>Type:</td><td>{p.t_type}</td></tr>}
                    {p.t_tenstatus && <tr><td>Status:</td><td>{p.t_tenstatus}</td></tr>}
                    {p.t_survstatus && <tr><td>Survey Status:</td><td>{p.t_survstatus}</td></tr>}
                    <tr><td>Area:</td><td>{areaHa}</td></tr>
                    {p.t_holdername && <tr><td>Holder(s):</td><td>{displayTenementHolder(p.t_holdername)}</td></tr>}
                    {p.t_startdate && <tr><td>Start Date:</td><td>{dateCell(p.t_startdate)}</td></tr>}
                    {p.t_enddate && <tr><td>End Date:</td><td>{dateCell(p.t_enddate)}</td></tr>}
                    {p.t_grantdate && <tr><td>Grant Date:</td><td>{dateCell(p.t_grantdate)}</td></tr>}
                    <tr>
                        <td>Amalgamation:</td>
                        <td>
                            <strong>Pending</strong>
                            {p.amalgids ? ` — ${p.amalgids}` : (p.amalgid ? ` — ${p.amalgid}` : '')}
                            {p.pending_since && <div style={{ color: '#6b6b6b', fontSize: 12 }}>since {dateCell(p.pending_since)}</div>}
                        </td>
                    </tr>
                </tbody>
            </Table>
        </Popup>
    );
}

/**
 * Is this collection in [lat, lng] rather than the [lng, lat] GeoJSON wants?
 *
 * Decided from the first coordinate pair found. A WA longitude is 112..129 and
 * a WA latitude is -35..-13, so only one reading of a pair can be valid: if the
 * first value is inside +/-90 and the second is outside it, the pair is the
 * wrong way round. Anything ambiguous is left alone - passing good coordinates
 * through untouched is always safe, whereas swapping them is not.
 */
function needsAxisSwap(features) {
    const firstPair = (coords) => {
        if (!coords) return null;
        if (typeof coords[0] === 'number' && typeof coords[1] === 'number') return coords;
        for (const c of coords) {
            const found = firstPair(c);
            if (found) return found;
        }
        return null;
    };
    for (const f of features) {
        const pair = firstPair(f.geometry && f.geometry.coordinates);
        if (pair) return Math.abs(pair[0]) <= 90 && Math.abs(pair[1]) > 90;
    }
    return false;
}

/**
 * Query the SLIP WFS server for amalgamations pending tenements
 * Fetches all features (no bbox filter) since there are only ~150-200 total
 */
export function queryAmalgamationsPendingWFS(setStateGeoJSON) {
    return SlipWfsServer.get("", {
        params: {
            service: 'WFS',
            version: '2.0.0',
            request: 'GetFeature',
            typeNames: 'esri:Tenements_-_Amalgamation_pending__DMIRS-031_',
            outputFormat: 'GEOJSON',
            srsName: 'EPSG:4326'
        }
    }).then((response) => {
        if (response.data.features && response.data.features.length > 0) {
            // SLIP'S AXIS ORDER IS NOT STABLE, so it is detected rather than
            // assumed. This used to return [lat, lng] and was swapped
            // unconditionally; it now returns [lng, lat], and the swap turned
            // every latitude into 116..128 - outside the valid -90..90 - so
            // MapLibre could not place any of the 198 features and the layer
            // rendered empty with no error anywhere.
            //
            // The pair itself says which way round it is: every WA longitude
            // is >90 in absolute terms (112..129) and every WA latitude is <90
            // (-35..-13). So a first value that cannot be a longitude, paired
            // with a second that cannot be a latitude, is the swapped form.
            const swapped = needsAxisSwap(response.data.features);
            const fixedFeatures = !swapped ? response.data.features : response.data.features.map(feature => {
                const fixCoordinates = (coords) => {
                    if (!coords) return coords;

                    // Check if this is a coordinate pair [lat, lng]
                    if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
                        return [coords[1], coords[0]]; // Swap to [lng, lat]
                    }

                    // Recursively fix nested arrays (for Polygons and MultiPolygons)
                    return coords.map(fixCoordinates);
                };

                return {
                    ...feature,
                    geometry: {
                        ...feature.geometry,
                        coordinates: fixCoordinates(feature.geometry.coordinates)
                    }
                };
            });

            const fixedData = {
                ...response.data,
                features: fixedFeatures
            };

            setStateGeoJSON(fixedData);
            return fixedData;
        }

        if (response.data.features !== undefined) {
            setStateGeoJSON(response.data);
        }
        return response.data;
    }).catch((error) => {
        console.error('Amalgamations Pending WFS query error:', error);
        // Return empty GeoJSON to keep UI functional
        const emptyResult = {
            type: "FeatureCollection",
            features: []
        };
        setStateGeoJSON(emptyResult);
        return emptyResult;
    });
}

/**
 * AmalgamationsPendingOverlay component
 * Displays tenements with amalgamation pending status from SLIP WFS
 */
export function AmalgamationsPendingOverlay({ overlayVisibility, selectedAmalgamation, geoJson, setGeoJson, tenementGeoJson }) {
    // Fetch all amalgamations pending features once on mount
    useEffect(() => {
        if (!geoJson || geoJson.features.length === 0) {
            queryAmalgamationsPendingWFS(setGeoJson);
        }
    }, []); // Empty dependency array - fetch once on mount

    // Create point geometries for labels from polygon centroids (must be before early return)
    // What is drawn: only amalgamations whose tenement is in the tenement layer
    // as currently filtered and searched. The amalgamation layer used to ignore
    // the tenement filters entirely, so a search for one holder still drew every
    // amalgamation in the state on top. The joined tenement's details go on as
    // t_* properties for the popup, and a tenement with several pending
    // amalgamations (E 28/1895 has two) is drawn once, listing them all.
    const shown = useMemo(() => {
        const empty = { type: 'FeatureCollection', features: [] };
        if (!geoJson?.features?.length || !tenementGeoJson?.features?.length) return empty;
        const tenements = new Map(tenementGeoJson.features.map((t) => [tenementKey(t.properties.fmt_tenid), t.properties]));
        const byTenement = new Map();
        geoJson.features.forEach((f) => {
            const key = tenementKey(f.properties.fmt_tenid);
            const t = tenements.get(key);
            if (!t) return;
            const prev = byTenement.get(key);
            if (prev) {
                prev.properties.amalgids = [...new Set([...prev.properties.amalgids.split(', '), f.properties.amalgid].filter(Boolean))].join(', ');
                if (Date.parse(f.properties.start_stamp) < Date.parse(prev.properties.pending_since)) {
                    prev.properties.pending_since = f.properties.start_stamp;
                }
                return;
            }
            byTenement.set(key, {
                ...f,
                properties: {
                    ...f.properties,
                    amalgids: f.properties.amalgid || '',
                    pending_since: f.properties.start_stamp,
                    t_type: t.type, t_tenstatus: t.tenstatus, t_survstatus: t.survstatus,
                    t_legal_area: t.legal_area, t_unit_of_me: t.unit_of_me, t_holdername: t.holdername,
                    t_startdate: t.startdate, t_enddate: t.enddate, t_grantdate: t.grantdate,
                },
            });
        });
        return { type: 'FeatureCollection', features: [...byTenement.values()] };
    }, [geoJson, tenementGeoJson]);

    const labelsGeoJson = useMemo(() => {
        const geoJson = shown;
        if (!geoJson || !geoJson.features || geoJson.features.length === 0) {
            return {
                type: 'FeatureCollection',
                features: []
            };
        }

        const labelFeatures = geoJson.features.map(feature => {
            const centerOfMass = turf.centerOfMass(feature);
            return {
                type: 'Feature',
                geometry: centerOfMass.geometry,
                properties: feature.properties
            };
        });

        return {
            type: 'FeatureCollection',
            features: labelFeatures
        };
    }, [shown]);

    // Calculate visibility based on both tenements and amalgamationsPending checkboxes
    const isVisible = overlayVisibility.tenements && overlayVisibility.amalgamationsPending;

    // Don't render if no features loaded yet
    if (!geoJson.features || geoJson.features.length === 0) {
        return null;
    }

    return (
        <>
            <Source id="amalgamations-pending" type="geojson" data={shown}>
                <Layer
                    id='amalgamations-pending-fill'
                    type='fill'
                    paint={{ 'fill-color': '#0000FF', 'fill-opacity': 0.2 }}
                    layout={{ visibility: isVisible ? 'visible' : 'none' }}
                />
                <Layer
                    id='amalgamations-pending-outline'
                    type='line'
                    paint={{ 'line-color': "#0000FF", 'line-width': 2 }}
                    layout={{ visibility: isVisible ? 'visible' : 'none' }}
                />
                {/* Selected amalgamation highlight - yellow */}
                <Layer
                    id='selected-amalgamation-fill'
                    type='fill'
                    filter={['==', 'OBJECTID', selectedAmalgamation.properties.OBJECTID || 0]}
                    paint={{ 'fill-color': '#FFFF00', 'fill-opacity': 0.2 }}
                    layout={{ visibility: isVisible ? 'visible' : 'none' }}
                />
                <Layer
                    id='selected-amalgamation-outline'
                    type='line'
                    filter={['==', 'OBJECTID', selectedAmalgamation.properties.OBJECTID || 0]}
                    paint={{ 'line-color': "#FFFF00", 'line-width': 2 }}
                    layout={{ visibility: isVisible ? 'visible' : 'none' }}
                />
            </Source>

            {/* Separate source for labels using point geometries */}
            <Source id="amalgamations-pending-labels" type="geojson" data={labelsGeoJson}>
                <Layer
                    id='amalgamations-pending-labels'
                    minzoom={10.6}
                    type='symbol'
                    layout={{
                        'text-field': ['get', 'fmt_tenid'],
                        'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
                        'text-size': 12,
                        'text-anchor': 'center',
                        'text-allow-overlap': true,
                        'text-ignore-placement': true,
                        visibility: isVisible && overlayVisibility.tenementLabels ? 'visible' : 'none'
                    }}
                    paint={{
                        'text-color': 'rgba(100,150,255,1)',
                        'text-halo-color': 'rgba(0,0,0,1)',
                        'text-halo-width': 2
                    }}
                />
            </Source>
        </>
    );
}
